import type { ComponentType } from "react";
import { Bot, BriefcaseBusiness, ChartLine, FileSpreadsheet, Image as ImageIcon, KeyRound, Mail, MessageSquare, Plug, Search, Server, Telescope, Users, Video } from "lucide-react";
import { SiBrevo, SiBuffer, SiCanva, SiCloudflare, SiElevenlabs, SiFigma, SiGithub, SiGoogle, SiGooglecloud, SiIntercom, SiLinear, SiNotion, SiSemrush, SiSentry, SiStripe, SiWebflow, SiX, SiYoutube, SiZapier } from "react-icons/si";
import type {
  WorkspaceIntegration,
  WorkspaceIntegrationCatalogItem,
  WorkspaceIntegrationPolicy,
  WorkspaceIntegrationPolicyState,
  WorkspaceIntegrationProbeTool,
} from "../lib/api";

// Catalog icons may be Lucide outlines or react-icons brand marks; both render
// from a className-only component.
type CatalogIcon = ComponentType<{ className?: string }>;

type CatalogScope = "platform" | "workspace" | "personal";
type AuthKind = "bearer" | "oauth";
export type PermissionLevel = "off" | "read" | "read_write";

type OAuthPermissionOption = {
  value: PermissionLevel;
  label: string;
  description: string;
};

type OAuthPermissionService = {
  id: string;
  label: string;
  description: string;
  defaultLevel: PermissionLevel;
  options: OAuthPermissionOption[];
};

export type OAuthPermissionSelection = Record<string, PermissionLevel>;

type CatalogField = {
  id: string;
  label: string;
  type: "text" | "password" | "textarea" | "select";
  placeholder: string;
  required?: boolean;
  secret?: boolean;
  configKey?: string;
  options?: Array<{ value: string; label: string }>;
};

export type CatalogEntry = {
  id: string;
  name: string;
  description: string;
  category: string;
  scope: CatalogScope;
  connectionSlug?: string;
  oauthProviderSlug?: string;
  googleService?: "gmail" | "drive" | "calendar" | "docs" | "sheets";
  Icon: CatalogIcon;
  authKind: AuthKind;
  transport: "http" | "mcp-remote";
  kind?: string;
  endpoint?: string;
  tokenType?: string;
  toolManifest?: Array<Record<string, unknown>>;
  developer: string;
  website: string;
  privacy: string;
  terms: string;
  usageSuggestion: string;
  tools: Array<{ name: string; description: string; access: "Read" | "Write"; mode: "Interactive" | "Background"; source?: string }>;
  fields?: CatalogField[];
  oauthPermissions?: OAuthPermissionService[];
};

// Search aliases (integrations.sh pattern): substring matching stays exact —
// no fuzzy — but common shorthand and brand names resolve to the right app.
export const CATALOG_SEARCH_ALIASES: Record<string, string[]> = {
  github: ["gh", "git", "repos", "pull requests"],
  gmail: ["email", "mail", "google mail"],
  agentmail: ["email", "inbox", "drafts", "agent email"],
  "google-drive": ["gdrive", "drive", "files", "docs"],
  "google-calendar": ["gcal", "calendar", "cal", "events"],
  "google-docs": ["docs", "documents", "google documents"],
  "google-sheets": ["sheets", "spreadsheets", "google spreadsheets"],
  "google-workspace": ["gsuite", "google suite", "google docs", "google sheets"],
  notion: ["wiki", "pages"],
  apollo: ["prospecting", "leads", "sales", "contacts"],
  brevo: ["sendinblue", "email campaigns"],
  cloudflare: ["cf", "workers", "kv"],
  linear: ["issues", "tickets", "project", "roadmap"],
  brave: ["brave search", "web search", "search api"],
  tavily: ["web search", "search api", "research"],
  exa: ["web search", "neural search", "search api"],
  "gemini-search": ["google search", "gemini", "grounding", "web search"],
  firecrawl: ["web search", "crawl", "scrape", "search api"],
  youtube: ["video", "videos"],
  slack: ["chat", "messages", "channels"],
  jira: ["issues", "tickets", "atlassian"],
  linkedin: ["social", "sales", "profiles"],
  x: ["twitter", "tweets", "social"],
  hubspot: ["crm", "contacts", "sales"],
  outlook: ["email", "mail", "microsoft"],
  "microsoft-teams": ["teams", "chat", "microsoft"],
  trello: ["boards", "cards", "kanban"],
  tiktok: ["social", "video"],
  instagram: ["social", "photos"],
  salesforce: ["crm", "sales", "accounts"],
  sentry: ["errors", "crashes", "monitoring", "issues"],
  stripe: ["payments", "billing", "customers", "invoices"],
  webflow: ["cms", "website", "collections", "site"],
  eodhd: ["financial data", "earnings", "fundamentals", "stocks", "market news"],
  phantombuster: ["scraping", "automation", "linkedin scraping", "signals"],
  "fal-ai": ["image generation", "fal", "diffusion", "images"],
  "success-ai": ["outreach", "campaigns", "cold email"],
  canva: ["designs", "graphics", "brand assets", "social images"],
  figma: ["design files", "components", "mockups"],
  buffer: ["social scheduling", "posts", "publish"],
  intercom: ["support", "conversations", "customer messaging"],
  klaviyo: ["email marketing", "sms", "campaigns", "flows"],
  semrush: ["seo", "keywords", "backlinks", "competitive research"],
  zapier: ["automation", "zaps", "workflows"],
  elevenlabs: ["voice", "text to speech", "tts", "audio"],
  "custom-mcp": ["mcp", "model context protocol", "server"],
  "openapi-import": ["swagger", "rest", "api spec"],
  "graphql-import": ["gql", "graphql"],
};

// Whether a catalog entry matches a user search query. Every whitespace token
// must match somewhere (intent search: "send email" finds the app whose tool
// sends email even though no single field holds the phrase). Short tokens
// match identity fields only — substring over prose makes "gh" hit every
// description containing "through".
export function catalogSearchMatches(entry: CatalogEntry, rawQuery: string): boolean {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return true;
  const identity = [entry.name, entry.id, ...(CATALOG_SEARCH_ALIASES[entry.id] ?? [])];
  const prose = [
    entry.description,
    entry.category,
    entry.developer,
    ...entry.tools.flatMap((tool) => [tool.name, tool.description]),
    ...(entry.oauthPermissions ?? []).flatMap((permission) => [permission.label, permission.description]),
  ];
  const matchesToken = (token: string) => {
    const values = token.length >= 3 ? [...identity, ...prose] : identity;
    return values.some((value) => value.toLowerCase().includes(token));
  };
  return query.split(/\s+/).every(matchesToken);
}

// Catalog format classification for the filter pills (integrations.sh's
// format pills with live counts).
export type CatalogFormat = "prebuilt" | "mcp" | "api";

export function catalogEntryFormat(entry: CatalogEntry): CatalogFormat {
  if (catalogEntryIsImport(entry)) return "api";
  if (entry.transport === "mcp-remote") return "mcp";
  return "prebuilt";
}

export const CUSTOM_MCP_CATALOG_ID = "custom-mcp";
export const GOOGLE_WORKSPACE_CONNECTION_SLUG = "google-workspace";

// Entries that connect through the probe -> OAuth remote-MCP flow: the generic
// custom-MCP entry, plus curated OAuth apps whose catalog carries the vendor's
// official hosted MCP endpoint (Notion, Linear, Jira, ...). Bearer curated MCP
// entries (GitHub) still connect via a plain token save.
export function catalogEntryUsesMCPConnectFlow(entry: CatalogEntry | null | undefined): boolean {
  if (!entry) return false;
  if (entry.id === CUSTOM_MCP_CATALOG_ID || entry.kind === "custom-mcp") return true;
  return entry.transport === "mcp-remote" && Boolean(entry.endpoint) && entry.authKind === "oauth";
}

const MAX_CUSTOM_MCP_ENABLED_TOOLS = 25;
const CUSTOM_MCP_SNAPSHOT_STALE_MS = 7 * 24 * 60 * 60 * 1000;

const CUSTOM_MCP_FIELDS: CatalogField[] = [
  { id: "url", label: "Remote MCP URL", type: "text", placeholder: "https://mcp.example.com", required: true, configKey: "display_target" },
  { id: "token", label: "Bearer token or API key", type: "password", placeholder: "Optional unless the server requires auth", secret: true },
];

const IMPORT_AUTH_OPTIONS = [
  { value: "none", label: "No auth" },
  { value: "bearer", label: "Bearer token" },
  { value: "api_key_header", label: "API key header" },
  { value: "oauth", label: "OAuth access token" },
];

const OPENAPI_IMPORT_FIELDS: CatalogField[] = [
  { id: "slug", label: "Connection slug", type: "text", placeholder: "linear-api", required: true },
  { id: "display_name", label: "Display name", type: "text", placeholder: "Linear API", required: true },
  { id: "spec_url", label: "Spec URL", type: "text", placeholder: "https://api.example.com/openapi.json" },
  { id: "base_url", label: "Base URL override", type: "text", placeholder: "https://api.example.com" },
  { id: "auth_type", label: "Auth mapping", type: "select", placeholder: "No auth", options: IMPORT_AUTH_OPTIONS },
  { id: "auth_header", label: "Auth header", type: "text", placeholder: "Authorization or X-API-Key" },
  { id: "token", label: "Token or API key", type: "password", placeholder: "Stored encrypted on OCM", secret: true },
  { id: "spec_text", label: "Uploaded spec", type: "textarea", placeholder: "Paste OpenAPI JSON or YAML" },
];

const GRAPHQL_IMPORT_FIELDS: CatalogField[] = [
  { id: "slug", label: "Connection slug", type: "text", placeholder: "records-graphql", required: true },
  { id: "display_name", label: "Display name", type: "text", placeholder: "Records GraphQL", required: true },
  { id: "endpoint", label: "GraphQL endpoint", type: "text", placeholder: "https://api.example.com/graphql", required: true },
  { id: "auth_type", label: "Auth mapping", type: "select", placeholder: "No auth", options: IMPORT_AUTH_OPTIONS },
  { id: "auth_header", label: "Auth header", type: "text", placeholder: "Authorization or X-API-Key" },
  { id: "token", label: "Token or API key", type: "password", placeholder: "Stored encrypted on OCM", secret: true },
  { id: "spec_text", label: "Schema or introspection JSON", type: "textarea", placeholder: "Paste GraphQL SDL or introspection JSON", required: true },
];

// Curated remote-MCP catalog entries have a fixed URL (no URL input). Bearer ones
// (e.g. GitHub PAT, Brevo API key) show only a token field; OAuth ones show none.
const CURATED_MCP_BEARER_FIELDS: CatalogField[] = [
  { id: "token", label: "Token or API key", type: "password", placeholder: "Paste your token / API key", secret: true },
];

const WEB_SEARCH_API_KEY_FIELDS: CatalogField[] = [
  { id: "token", label: "API key", type: "password", placeholder: "Paste your provider API key", secret: true, required: true },
];

const EODHD_TOOL_MANIFEST = [
  {
    name: "eodhd_earnings_calendar",
    description: "Upcoming and historical earnings by date range and symbols.",
    parameters: {
      type: "object",
      properties: {
        from: { type: "string", description: "Start date YYYY-MM-DD" },
        to: { type: "string", description: "End date YYYY-MM-DD" },
        symbols: { type: "string", description: "Comma-separated symbols, e.g. AAPL.US,MSFT.US" },
      },
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/api/calendar/earnings",
      query: {
        from: { source: "arg", name: "from", omit_empty: true },
        to: { source: "arg", name: "to", omit_empty: true },
        symbols: { source: "arg", name: "symbols", omit_empty: true },
        fmt: { source: "arg", name: "fmt", default: "json" },
      },
      auth: { type: "api_key_query", query_param: "api_token", required: true },
    },
  },
  {
    name: "eodhd_fundamentals",
    description: "Company fundamentals for one symbol.",
    parameters: {
      type: "object",
      properties: {
        symbol: { type: "string", description: "Symbol with exchange suffix, e.g. AAPL.US" },
      },
      required: ["symbol"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/api/fundamentals/{symbol}",
      path_params: {
        symbol: { source: "arg", name: "symbol", required: true },
      },
      query: {
        fmt: { source: "arg", name: "fmt", default: "json" },
      },
      auth: { type: "api_key_query", query_param: "api_token", required: true },
    },
  },
  {
    name: "eodhd_news",
    description: "Market and company news.",
    parameters: {
      type: "object",
      properties: {
        symbols: { type: "string", description: "Comma-separated symbols, e.g. AAPL.US" },
        from: { type: "string", description: "Start date YYYY-MM-DD" },
        to: { type: "string", description: "End date YYYY-MM-DD" },
        limit: { type: "integer", minimum: 1, maximum: 100 },
      },
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/api/news",
      query: {
        s: { source: "arg", name: "symbols", omit_empty: true },
        from: { source: "arg", name: "from", omit_empty: true },
        to: { source: "arg", name: "to", omit_empty: true },
        limit: { source: "arg", name: "limit", default: 10, min: 1, max: 100 },
        fmt: { source: "arg", name: "fmt", default: "json" },
      },
      auth: { type: "api_key_query", query_param: "api_token", required: true },
    },
  },
];

const PHANTOMBUSTER_TOOL_MANIFEST = [
  {
    name: "phantombuster_list_agents",
    description: "List Phantombuster agents in the account.",
    parameters: { type: "object", properties: {}, additionalProperties: false },
    request: {
      method: "GET",
      path: "/api/v2/agents/fetch-all",
      auth: { type: "api_key_header", header: "X-Phantombuster-Key", required: true },
    },
  },
  {
    name: "phantombuster_fetch_output",
    description: "Fetch an agent's latest output.",
    parameters: {
      type: "object",
      properties: {
        agent_id: { type: "string", description: "Phantombuster agent id" },
      },
      required: ["agent_id"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/api/v2/agents/fetch-output",
      query: {
        id: { source: "arg", name: "agent_id" },
      },
      auth: { type: "api_key_header", header: "X-Phantombuster-Key", required: true },
    },
  },
  {
    name: "phantombuster_launch_agent",
    description: "Launch an agent run (requires write).",
    access: "write",
    parameters: {
      type: "object",
      properties: {
        agent_id: { type: "string", description: "Phantombuster agent id" },
      },
      required: ["agent_id"],
      additionalProperties: false,
    },
    request: {
      method: "POST",
      path: "/api/v2/agents/launch",
      body: {
        fields: [
          { path: "id", value: { source: "arg", name: "agent_id" }, required: true },
        ],
      },
      auth: { type: "api_key_header", header: "X-Phantombuster-Key", required: true },
    },
  },
];

const FAL_TOOL_MANIFEST = [
  {
    name: "fal_generate_image",
    description: "Generate an image from a text prompt with a fal.ai hosted model.",
    access: "write",
    parameters: {
      type: "object",
      properties: {
        prompt: { type: "string", description: "Text prompt for the image" },
        model: { type: "string", description: "fal model path, e.g. fal-ai/flux/dev" },
        image_size: { type: "string", description: "e.g. square_hd, landscape_4_3" },
      },
      required: ["prompt"],
      additionalProperties: false,
    },
    request: {
      method: "POST",
      path: "/{model}",
      path_params: {
        model: { source: "arg", name: "model", default: "fal-ai/flux/dev" },
      },
      body: {
        fields: [
          { path: "prompt", value: { source: "arg", name: "prompt" }, required: true },
          { path: "image_size", value: { source: "arg", name: "image_size", omit_empty: true } },
        ],
      },
      auth: { type: "bearer", scheme: "Key", required: true },
    },
  },
];

const YOUTUBE_TOOL_MANIFEST = [
  {
    name: "youtube_search",
    description: "Search YouTube videos.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string", description: "Search terms" },
        max_results: { type: "integer", minimum: 1, maximum: 50 },
      },
      required: ["query"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/search",
      query: {
        q: { source: "arg", name: "query" },
        maxResults: { source: "arg", name: "max_results", default: 10, min: 1, max: 50 },
        part: { source: "arg", name: "part", default: "snippet" },
        type: { source: "arg", name: "type", default: "video" },
      },
      auth: { type: "api_key_query", query_param: "key", required: true },
    },
  },
  {
    name: "youtube_video_details",
    description: "Fetch snippet, content details, and statistics for videos.",
    parameters: {
      type: "object",
      properties: {
        video_ids: { type: "string", description: "Comma-separated YouTube video ids" },
      },
      required: ["video_ids"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/videos",
      query: {
        id: { source: "arg", name: "video_ids" },
        part: { source: "arg", name: "part", default: "snippet,contentDetails,statistics" },
      },
      auth: { type: "api_key_query", query_param: "key", required: true },
    },
  },
];

const X_TOOL_MANIFEST = [
  {
    name: "x_search_recent",
    description: "Search recent X posts (last 7 days).",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string", description: "X search query, e.g. from:user or keywords" },
        max_results: { type: "integer", minimum: 10, maximum: 100 },
      },
      required: ["query"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/2/tweets/search/recent",
      query: {
        query: { source: "arg", name: "query" },
        max_results: { source: "arg", name: "max_results", default: 10, min: 10, max: 100 },
        "tweet.fields": { source: "arg", name: "tweet_fields", default: "created_at,public_metrics,author_id" },
      },
      auth: { type: "bearer", required: true },
    },
  },
  {
    name: "x_user_by_username",
    description: "Look up an X account by username.",
    parameters: {
      type: "object",
      properties: {
        username: { type: "string", description: "Handle without @" },
      },
      required: ["username"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/2/users/by/username/{username}",
      path_params: {
        username: { source: "arg", name: "username", required: true },
      },
      query: {
        "user.fields": { source: "arg", name: "user_fields", default: "description,public_metrics,verified" },
      },
      auth: { type: "bearer", required: true },
    },
  },
  {
    name: "x_user_tweets",
    description: "Fetch an account's recent posts by user id.",
    parameters: {
      type: "object",
      properties: {
        user_id: { type: "string", description: "Numeric X user id (from x_user_by_username)" },
        max_results: { type: "integer", minimum: 5, maximum: 100 },
      },
      required: ["user_id"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/2/users/{user_id}/tweets",
      path_params: {
        user_id: { source: "arg", name: "user_id", required: true },
      },
      query: {
        max_results: { source: "arg", name: "max_results", default: 10, min: 5, max: 100 },
        "tweet.fields": { source: "arg", name: "tweet_fields", default: "created_at,public_metrics" },
      },
      auth: { type: "bearer", required: true },
    },
  },
];

const ELEVENLABS_TOOL_MANIFEST = [
  {
    name: "elevenlabs_list_voices",
    description: "List available ElevenLabs voices.",
    parameters: { type: "object", properties: {}, additionalProperties: false },
    request: {
      method: "GET",
      path: "/v1/voices",
      auth: { type: "api_key_header", header: "xi-api-key", required: true },
    },
  },
  {
    name: "elevenlabs_list_models",
    description: "List available ElevenLabs speech models.",
    parameters: { type: "object", properties: {}, additionalProperties: false },
    request: {
      method: "GET",
      path: "/v1/models",
      auth: { type: "api_key_header", header: "xi-api-key", required: true },
    },
  },
];

const BRAVE_SEARCH_TOOL_MANIFEST = [
  {
    name: "brave_search",
    description: "Search the web with Brave Search.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string" },
        count: { type: "integer", minimum: 1, maximum: 20 },
        freshness: { type: "string", enum: ["pd", "pw", "pm", "py"] },
        country: { type: "string" },
        search_lang: { type: "string" },
        safe_search: { type: "string", enum: ["off", "moderate", "strict"] },
      },
      required: ["query"],
      additionalProperties: false,
    },
    request: {
      method: "GET",
      path: "/res/v1/web/search",
      query: {
        q: { source: "arg", name: "query" },
        count: { source: "arg", name: "count", default: 10, min: 1, max: 20 },
        freshness: { source: "arg", name: "freshness", omit_empty: true, enum: ["pd", "pw", "pm", "py"] },
        country: { source: "arg", name: "country", omit_empty: true },
        search_lang: { source: "arg", name: "search_lang", omit_empty: true },
        safesearch: { source: "arg", name: "safe_search", default: "moderate", enum: ["off", "moderate", "strict"] },
      },
      auth: { type: "api_key_header", header: "X-Subscription-Token", required: true },
      response: {
        fields: {
          query: "query.original",
          more_results_available: "query.more_results_available",
          results: "web.results",
        },
      },
    },
  },
];

const TAVILY_SEARCH_TOOL_MANIFEST = [
  {
    name: "tavily_search",
    description: "Search the web with Tavily Search.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string" },
        max_results: { type: "integer", minimum: 1, maximum: 20 },
        search_depth: { type: "string", enum: ["basic", "advanced", "fast", "ultra-fast"] },
        topic: { type: "string", enum: ["general", "news", "finance"] },
        time_range: { type: "string", enum: ["day", "week", "month", "year", "d", "w", "m", "y"] },
        include_answer: { type: "boolean" },
      },
      required: ["query"],
      additionalProperties: false,
    },
    request: {
      method: "POST",
      path: "/search",
      auth: { type: "bearer", scheme: "Bearer", required: true },
      body: {
        fields: [
          { path: "query", required: true, value: { source: "arg", name: "query" } },
          { path: "max_results", raw: true, value: { source: "arg", name: "max_results", default: 5 } },
          { path: "search_depth", value: { source: "arg", name: "search_depth", default: "basic", enum: ["basic", "advanced", "fast", "ultra-fast"] } },
          { path: "topic", value: { source: "arg", name: "topic", default: "general", enum: ["general", "news", "finance"] } },
          { path: "time_range", value: { source: "arg", name: "time_range", omit_empty: true, enum: ["day", "week", "month", "year", "d", "w", "m", "y"] } },
          { path: "include_answer", raw: true, value: { source: "arg", name: "include_answer", default: false } },
        ],
      },
    },
  },
];

const EXA_SEARCH_TOOL_MANIFEST = [
  {
    name: "exa_search",
    description: "Search the web with Exa.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string" },
        num_results: { type: "integer", minimum: 1, maximum: 100 },
        type: { type: "string", enum: ["instant", "fast", "auto", "deep-lite", "deep", "deep-reasoning"] },
        category: { type: "string" },
        include_highlights: { type: "boolean" },
      },
      required: ["query"],
      additionalProperties: false,
    },
    request: {
      method: "POST",
      path: "/search",
      auth: { type: "api_key_header", header: "x-api-key", required: true },
      body: {
        fields: [
          { path: "query", required: true, value: { source: "arg", name: "query" } },
          { path: "numResults", raw: true, value: { source: "arg", name: "num_results", default: 10 } },
          { path: "type", value: { source: "arg", name: "type", default: "auto", enum: ["instant", "fast", "auto", "deep-lite", "deep", "deep-reasoning"] } },
          { path: "category", value: { source: "arg", name: "category", omit_empty: true } },
          { path: "contents.highlights", raw: true, value: { source: "arg", name: "include_highlights", default: true } },
        ],
      },
    },
  },
];

const FIRECRAWL_SEARCH_TOOL_MANIFEST = [
  {
    name: "firecrawl_search",
    description: "Search the web with Firecrawl Search.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string" },
        limit: { type: "integer", minimum: 1, maximum: 100 },
        include_domains: { type: "array", items: { type: "string" } },
        exclude_domains: { type: "array", items: { type: "string" } },
      },
      required: ["query"],
      additionalProperties: false,
    },
    request: {
      method: "POST",
      path: "/v2/search",
      auth: { type: "bearer", scheme: "Bearer", required: true },
      body: {
        fields: [
          { path: "query", required: true, value: { source: "arg", name: "query" } },
          { path: "limit", raw: true, value: { source: "arg", name: "limit", default: 10 } },
          { path: "sources", raw: true, value: { value: ["web"] } },
          { path: "includeDomains", raw: true, value: { source: "arg", name: "include_domains", omit_empty: true } },
          { path: "excludeDomains", raw: true, value: { source: "arg", name: "exclude_domains", omit_empty: true } },
        ],
      },
    },
  },
];

const offReadWriteOptions: OAuthPermissionOption[] = [
  { value: "off", label: "Off", description: "Do not request access." },
  { value: "read", label: "Read", description: "Allow read-only access." },
  { value: "read_write", label: "Read + write", description: "Allow changes through workspace tools." },
];

export const CATALOG: CatalogEntry[] = [
  {
    id: "github",
    name: "GitHub",
    description: "Repository access for code, issues, and pull requests.",
    category: "Featured",
    scope: "workspace",
    Icon: SiGithub,
    authKind: "bearer",
    transport: "http",
    kind: "github",
    endpoint: "https://api.github.com",
    tokenType: "token",
    developer: "GitHub",
    website: "github.com",
    privacy: "github.com/site/privacy",
    terms: "docs.github.com/site-policy",
    usageSuggestion: "Review repository status, summarize open issues, and inspect pull request context.",
    tools: [
      { name: "get_repo", description: "Get the configured repository summary.", access: "Read", mode: "Interactive" },
      { name: "list_issues", description: "List issues for the configured repository.", access: "Read", mode: "Interactive" },
    ],
    fields: [
      { id: "owner", label: "Repository owner", type: "text", placeholder: "octocat", required: true },
      { id: "repo", label: "Repository name", type: "text", placeholder: "Hello-World", required: true },
      { id: "token", label: "Access token", type: "password", placeholder: "Optional for public repositories", secret: true },
    ],
    toolManifest: [
      {
        name: "get_repo",
        description: "Get the configured GitHub repository summary",
        parameters: {
          type: "object",
          additionalProperties: false,
        },
        request: {
          method: "GET",
          path: "/repos/{owner}/{repo}",
          headers: {
            Accept: "application/vnd.github+json",
            "X-GitHub-Api-Version": "2022-11-28",
            "User-Agent": "openclaw-workspace-integrations",
          },
          path_params: {
            owner: { source: "config", name: "owner" },
            repo: { source: "config", name: "repo" },
          },
          auth: { type: "bearer", scheme: "Bearer", required: false },
          response: {
            fields: {
              full_name: "full_name",
              description: "description",
              private: "private",
              html_url: "html_url",
              default_branch: "default_branch",
              open_issues_count: "open_issues_count",
              stargazers_count: "stargazers_count",
              forks_count: "forks_count",
              updated_at: "updated_at",
            },
          },
        },
      },
      {
        name: "list_issues",
        description: "List issues for the configured GitHub repository",
        parameters: {
          type: "object",
          properties: {
            state: { type: "string", enum: ["open", "closed", "all"] },
            per_page: { type: "integer", minimum: 1, maximum: 30 },
          },
          additionalProperties: false,
        },
        request: {
          method: "GET",
          path: "/repos/{owner}/{repo}/issues",
          headers: {
            Accept: "application/vnd.github+json",
            "X-GitHub-Api-Version": "2022-11-28",
            "User-Agent": "openclaw-workspace-integrations",
          },
          path_params: {
            owner: { source: "config", name: "owner" },
            repo: { source: "config", name: "repo" },
          },
          query: {
            state: { source: "arg", name: "state", default: "open", enum: ["open", "closed", "all"] },
            per_page: { source: "arg", name: "per_page", default: 10, min: 1, max: 30 },
          },
          auth: { type: "bearer", scheme: "Bearer", required: false },
          response: {
            wrap: "issues",
            exclude_if_present: "pull_request",
            fields: {
              number: "number",
              title: "title",
              state: "state",
              html_url: "html_url",
              user: "user.login",
              created_at: "created_at",
              updated_at: "updated_at",
            },
          },
        },
      },
    ],
  },
  {
    id: "gmail",
    connectionSlug: GOOGLE_WORKSPACE_CONNECTION_SLUG,
    googleService: "gmail",
    name: "Gmail",
    description: "Read Gmail messages and send email through workspace-approved tools.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: SiGoogle,
    authKind: "oauth",
    transport: "http",
    developer: "Google",
    website: "workspace.google.com/products/gmail",
    privacy: "policies.google.com/privacy",
    terms: "policies.google.com/terms",
    usageSuggestion: "Search inbox messages, read message details, and send approved email.",
    tools: [
      { name: "gmail_list_messages", description: "List Gmail messages, optionally by search query.", access: "Read", mode: "Interactive" },
      { name: "gmail_get_message", description: "Read a single Gmail message.", access: "Read", mode: "Interactive" },
      { name: "gmail_send_message", description: "Send an email (requires Read + send).", access: "Write", mode: "Interactive" },
    ],
    oauthPermissions: [
      {
        id: "gmail",
        label: "Gmail",
        description: "Email profile, messages, and sending permissions.",
        defaultLevel: "read",
        options: [
          { value: "off", label: "Off", description: "No Gmail access." },
          { value: "read", label: "Read", description: "Read Gmail messages and settings." },
          { value: "read_write", label: "Read + send", description: "Read Gmail and send email." },
        ],
      },
    ],
  },
  {
    id: "google-drive",
    connectionSlug: GOOGLE_WORKSPACE_CONNECTION_SLUG,
    googleService: "drive",
    name: "Google Drive",
    description: "List Drive metadata and create approved files through workspace tools.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: SiGoogle,
    authKind: "oauth",
    transport: "http",
    developer: "Google",
    website: "workspace.google.com/products/drive",
    privacy: "policies.google.com/privacy",
    terms: "policies.google.com/terms",
    usageSuggestion: "Find visible Drive files and create files in approved folders.",
    tools: [
      { name: "drive_list_files", description: "List visible Google Drive file metadata.", access: "Read", mode: "Interactive" },
      { name: "drive_create_file", description: "Create a Drive file (requires Read + write).", access: "Write", mode: "Interactive" },
    ],
    oauthPermissions: [
      {
        id: "drive",
        label: "Google Drive",
        description: "Drive file discovery and file operations.",
        defaultLevel: "read",
        options: offReadWriteOptions,
      },
    ],
  },
  {
    id: "google-calendar",
    connectionSlug: GOOGLE_WORKSPACE_CONNECTION_SLUG,
    googleService: "calendar",
    name: "Google Calendar",
    description: "Read and create calendar events through workspace-approved tools.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: SiGoogle,
    authKind: "oauth",
    transport: "http",
    developer: "Google",
    website: "workspace.google.com/products/calendar",
    privacy: "policies.google.com/privacy",
    terms: "policies.google.com/terms",
    usageSuggestion: "List upcoming events and create approved calendar events.",
    tools: [
      { name: "calendar_list_events", description: "List upcoming Google Calendar events.", access: "Read", mode: "Interactive" },
      { name: "calendar_create_event", description: "Create a calendar event (requires Read + write).", access: "Write", mode: "Interactive" },
    ],
    oauthPermissions: [
      {
        id: "calendar",
        label: "Calendar",
        description: "Calendar event lookup and updates.",
        defaultLevel: "read",
        options: offReadWriteOptions,
      },
    ],
  },
  {
    id: "google-docs",
    connectionSlug: GOOGLE_WORKSPACE_CONNECTION_SLUG,
    googleService: "docs",
    name: "Google Docs",
    description: "Read and create Google Docs documents through workspace-approved tools.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: SiGoogle,
    authKind: "oauth",
    transport: "http",
    developer: "Google",
    website: "workspace.google.com/products/docs",
    privacy: "policies.google.com/privacy",
    terms: "policies.google.com/terms",
    usageSuggestion: "Read Google Docs content and create approved documents.",
    tools: [
      { name: "docs_get_document", description: "Read a Google Doc.", access: "Read", mode: "Interactive" },
      { name: "docs_create_document", description: "Create a Google Doc (requires Read + write).", access: "Write", mode: "Interactive" },
    ],
    oauthPermissions: [
      {
        id: "docs",
        label: "Docs",
        description: "Google Docs document access.",
        defaultLevel: "off",
        options: offReadWriteOptions,
      },
    ],
  },
  {
    id: "google-sheets",
    connectionSlug: GOOGLE_WORKSPACE_CONNECTION_SLUG,
    googleService: "sheets",
    name: "Google Sheets",
    description: "Read and update Google Sheets ranges through workspace-approved tools.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: FileSpreadsheet,
    authKind: "oauth",
    transport: "http",
    developer: "Google",
    website: "workspace.google.com/products/sheets",
    privacy: "policies.google.com/privacy",
    terms: "policies.google.com/terms",
    usageSuggestion: "Read spreadsheet values and update approved ranges.",
    tools: [
      { name: "sheets_get_values", description: "Read values from a Google Sheets range.", access: "Read", mode: "Interactive" },
      { name: "sheets_update_values", description: "Update values in a Google Sheets range (requires Read + write).", access: "Write", mode: "Interactive" },
    ],
    oauthPermissions: [
      {
        id: "sheets",
        label: "Sheets",
        description: "Google Sheets range access.",
        defaultLevel: "off",
        options: offReadWriteOptions,
      },
    ],
  },
  {
    id: "google-workspace",
    name: "Google Workspace",
    description: "OAuth connection for Gmail, Drive, Calendar, Docs, and Sheets.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: SiGoogle,
    authKind: "oauth",
    transport: "http",
    developer: "Google",
    website: "workspace.google.com",
    privacy: "policies.google.com/privacy",
    terms: "policies.google.com/terms",
    usageSuggestion: "Read and send mail, find and create Drive files, and read or create calendar events, docs, and spreadsheets.",
    tools: [
      { name: "gmail_list_messages", description: "List Gmail messages, optionally by search query.", access: "Read", mode: "Interactive" },
      { name: "gmail_get_message", description: "Read a single Gmail message.", access: "Read", mode: "Interactive" },
      { name: "gmail_send_message", description: "Send an email (requires Gmail Read + send).", access: "Write", mode: "Interactive" },
      { name: "drive_list_files", description: "List visible Google Drive file metadata.", access: "Read", mode: "Interactive" },
      { name: "drive_create_file", description: "Create a Drive file (requires Drive Read + write).", access: "Write", mode: "Interactive" },
      { name: "calendar_list_events", description: "List upcoming Google Calendar events.", access: "Read", mode: "Interactive" },
      { name: "calendar_create_event", description: "Create a calendar event (requires Calendar Read + write).", access: "Write", mode: "Interactive" },
      { name: "docs_get_document", description: "Read a Google Doc (requires Docs Read).", access: "Read", mode: "Interactive" },
      { name: "docs_create_document", description: "Create a Google Doc (requires Docs Read + write).", access: "Write", mode: "Interactive" },
      { name: "sheets_get_values", description: "Read values from a Google Sheets range (requires Sheets Read).", access: "Read", mode: "Interactive" },
      { name: "sheets_update_values", description: "Update values in a Google Sheets range (requires Sheets Read + write).", access: "Write", mode: "Interactive" },
    ],
    oauthPermissions: [
      {
        id: "gmail",
        label: "Gmail",
        description: "Email profile, messages, and sending permissions.",
        defaultLevel: "read",
        options: [
          { value: "off", label: "Off", description: "No Gmail access." },
          { value: "read", label: "Read", description: "Read Gmail messages and settings." },
          { value: "read_write", label: "Read + send", description: "Read Gmail and send email." },
        ],
      },
      {
        id: "drive",
        label: "Google Drive",
        description: "Drive file discovery and file operations.",
        defaultLevel: "read",
        options: offReadWriteOptions,
      },
      {
        id: "calendar",
        label: "Calendar",
        description: "Calendar event lookup and updates.",
        defaultLevel: "read",
        options: offReadWriteOptions,
      },
      {
        id: "docs",
        label: "Docs",
        description: "Google Docs document access.",
        defaultLevel: "off",
        options: offReadWriteOptions,
      },
      {
        id: "sheets",
        label: "Sheets",
        description: "Google Sheets range access.",
        defaultLevel: "off",
        options: offReadWriteOptions,
      },
    ],
  },
  {
    id: "apollo",
    name: "Apollo.io",
    description: "Prospecting and account research through hosted MCP.",
    category: "Business & Operations",
    scope: "platform",
    Icon: Telescope,
    authKind: "oauth",
    transport: "mcp-remote",
    developer: "Apollo.io",
    website: "apollo.io",
    privacy: "apollo.io/privacy-policy",
    terms: "apollo.io/terms",
    usageSuggestion: "Research accounts and enrich lead context before drafting outreach.",
    tools: [
      { name: "search_people", description: "Search people records through Apollo.", access: "Read", mode: "Interactive" },
      { name: "search_organizations", description: "Search organization records through Apollo.", access: "Read", mode: "Interactive" },
    ],
  },
  {
    id: "brave",
    name: "Brave Search",
    description: "Search the web through the Brave Search API.",
    category: "Search",
    scope: "workspace",
    Icon: Search,
    authKind: "bearer",
    transport: "http",
    kind: "brave",
    endpoint: "https://api.search.brave.com",
    tokenType: "api_key_header",
    developer: "Brave",
    website: "brave.com/search/api",
    privacy: "brave.com/privacy",
    terms: "brave.com/terms-of-use",
    usageSuggestion: "Find current web pages and sources with Brave's independent index.",
    tools: [
      { name: "brave_search", description: "Search the web with Brave Search.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: BRAVE_SEARCH_TOOL_MANIFEST,
  },
  {
    id: "tavily",
    name: "Tavily",
    description: "Search the web through Tavily Search.",
    category: "Search",
    scope: "workspace",
    Icon: Search,
    authKind: "bearer",
    transport: "http",
    kind: "tavily",
    endpoint: "https://api.tavily.com",
    tokenType: "bearer",
    developer: "Tavily",
    website: "tavily.com",
    privacy: "tavily.com/privacy",
    terms: "tavily.com/terms",
    usageSuggestion: "Run agent-oriented web searches with optional answer synthesis.",
    tools: [
      { name: "tavily_search", description: "Search the web with Tavily Search.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: TAVILY_SEARCH_TOOL_MANIFEST,
  },
  {
    id: "exa",
    name: "Exa",
    description: "Search the web and retrieve content with Exa.",
    category: "Search",
    scope: "workspace",
    Icon: Search,
    authKind: "bearer",
    transport: "http",
    kind: "exa",
    endpoint: "https://api.exa.ai",
    tokenType: "api_key_header",
    developer: "Exa",
    website: "exa.ai",
    privacy: "exa.ai/privacy",
    terms: "exa.ai/terms",
    usageSuggestion: "Find high-relevance web results and retrieve result content.",
    tools: [
      { name: "exa_search", description: "Search the web with Exa.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: EXA_SEARCH_TOOL_MANIFEST,
  },
  {
    id: "firecrawl",
    name: "Firecrawl",
    description: "Search the web through Firecrawl Search.",
    category: "Search",
    scope: "workspace",
    Icon: Search,
    authKind: "bearer",
    transport: "http",
    kind: "firecrawl",
    endpoint: "https://api.firecrawl.dev",
    tokenType: "bearer",
    developer: "Firecrawl",
    website: "firecrawl.dev",
    privacy: "firecrawl.dev/privacy-policy",
    terms: "firecrawl.dev/terms-of-service",
    usageSuggestion: "Search web, news, or indexed content before scraping or citing sources.",
    tools: [
      { name: "firecrawl_search", description: "Search the web with Firecrawl Search.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: FIRECRAWL_SEARCH_TOOL_MANIFEST,
  },
  {
    id: "eodhd",
    name: "EODHD",
    description: "Financial data API — earnings calendar, fundamentals, and market news.",
    category: "Financial Data",
    scope: "workspace",
    Icon: ChartLine,
    authKind: "bearer",
    transport: "http",
    kind: "eodhd",
    endpoint: "https://eodhd.com",
    tokenType: "api_key_query",
    developer: "EODHD",
    website: "eodhd.com",
    privacy: "eodhd.com/financial-apis/privacy-policy",
    terms: "eodhd.com/financial-apis/terms-conditions",
    usageSuggestion: "Pull earnings calendars, fundamentals, and market news on a schedule or on demand.",
    tools: [
      { name: "eodhd_earnings_calendar", description: "Upcoming and historical earnings by date range and symbols.", access: "Read", mode: "Interactive" },
      { name: "eodhd_fundamentals", description: "Company fundamentals for a symbol.", access: "Read", mode: "Interactive" },
      { name: "eodhd_news", description: "Market and company news.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: EODHD_TOOL_MANIFEST,
  },
  {
    id: "phantombuster",
    name: "Phantombuster",
    description: "Launch Phantombuster agents and fetch their output for signal scraping workflows.",
    category: "Business & Operations",
    scope: "workspace",
    Icon: Bot,
    authKind: "bearer",
    transport: "http",
    kind: "phantombuster",
    endpoint: "https://api.phantombuster.com",
    tokenType: "api_key_header",
    developer: "Phantombuster",
    website: "phantombuster.com",
    privacy: "phantombuster.com/privacy",
    terms: "phantombuster.com/terms",
    usageSuggestion: "Run LinkedIn/X scraping agents and collect their output without cron plumbing.",
    tools: [
      { name: "phantombuster_list_agents", description: "List Phantombuster agents.", access: "Read", mode: "Interactive" },
      { name: "phantombuster_fetch_output", description: "Fetch an agent's latest output.", access: "Read", mode: "Interactive" },
      { name: "phantombuster_launch_agent", description: "Launch an agent run (requires write).", access: "Write", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: PHANTOMBUSTER_TOOL_MANIFEST,
  },
  {
    id: "fal-ai",
    name: "fal.ai",
    description: "Generate images with fal.ai hosted models.",
    category: "Media",
    scope: "workspace",
    Icon: ImageIcon,
    authKind: "bearer",
    transport: "http",
    kind: "fal-ai",
    endpoint: "https://fal.run",
    tokenType: "bearer",
    developer: "fal.ai",
    website: "fal.ai",
    privacy: "fal.ai/privacy",
    terms: "fal.ai/terms",
    usageSuggestion: "Generate brand and content images from prompts with hosted diffusion models.",
    tools: [
      { name: "fal_generate_image", description: "Generate an image from a prompt (requires write).", access: "Write", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: FAL_TOOL_MANIFEST,
  },
  {
    id: "youtube",
    name: "YouTube",
    description: "Search YouTube videos and fetch video details through the YouTube Data API.",
    category: "Media",
    scope: "workspace",
    Icon: SiYoutube,
    authKind: "bearer",
    transport: "http",
    kind: "youtube",
    endpoint: "https://www.googleapis.com/youtube/v3",
    tokenType: "api_key_query",
    developer: "Google",
    website: "youtube.com",
    privacy: "policies.google.com/privacy",
    terms: "developers.google.com/youtube/terms/api-services-terms-of-service",
    usageSuggestion: "Search videos and read stats for the content pipeline with a plain API key.",
    tools: [
      { name: "youtube_search", description: "Search YouTube videos.", access: "Read", mode: "Interactive" },
      { name: "youtube_video_details", description: "Fetch details and statistics for videos.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: YOUTUBE_TOOL_MANIFEST,
  },
  {
    id: "x",
    name: "X",
    description: "Search X (Twitter) posts and look up accounts through the X API v2.",
    category: "Marketing",
    scope: "workspace",
    Icon: SiX,
    authKind: "bearer",
    transport: "http",
    kind: "x",
    endpoint: "https://api.x.com",
    tokenType: "bearer",
    developer: "X",
    website: "x.com",
    privacy: "x.com/en/privacy",
    terms: "developer.x.com/en/developer-terms",
    usageSuggestion: "Track mentions, competitors, and audience signals with app-only API access.",
    tools: [
      { name: "x_search_recent", description: "Search recent posts (last 7 days).", access: "Read", mode: "Interactive" },
      { name: "x_user_by_username", description: "Look up an account by username.", access: "Read", mode: "Interactive" },
      { name: "x_user_tweets", description: "Fetch an account's recent posts.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: X_TOOL_MANIFEST,
  },
  {
    id: "elevenlabs",
    name: "ElevenLabs",
    description: "ElevenLabs voice AI — browse voices and models for audio content workflows.",
    category: "Media",
    scope: "workspace",
    Icon: SiElevenlabs,
    authKind: "bearer",
    transport: "http",
    kind: "elevenlabs",
    endpoint: "https://api.elevenlabs.io",
    tokenType: "api_key_header",
    developer: "ElevenLabs",
    website: "elevenlabs.io",
    privacy: "elevenlabs.io/privacy-policy",
    terms: "elevenlabs.io/terms-of-use",
    usageSuggestion: "Pick voices and models for audio content; speech generation lands once binary responses ship.",
    tools: [
      { name: "elevenlabs_list_voices", description: "List available voices.", access: "Read", mode: "Interactive" },
      { name: "elevenlabs_list_models", description: "List available speech models.", access: "Read", mode: "Interactive" },
    ],
    fields: WEB_SEARCH_API_KEY_FIELDS,
    toolManifest: ELEVENLABS_TOOL_MANIFEST,
  },
  {
    id: CUSTOM_MCP_CATALOG_ID,
    name: "Custom MCP server",
    description: "Connect a hosted MCP server by URL, review discovered tools, and add a bearer token when needed.",
    category: "Custom",
    scope: "workspace",
    Icon: Server,
    authKind: "bearer",
    transport: "mcp-remote",
    kind: "custom-mcp",
    developer: "Custom",
    website: "Remote MCP URL",
    privacy: "Provider policy",
    terms: "Provider terms",
    usageSuggestion: "Use a trusted remote MCP server after reviewing the tools it exposes.",
    tools: [],
    fields: CUSTOM_MCP_FIELDS,
  },
  {
    id: "openapi-import",
    name: "OpenAPI import",
    description: "Import an OpenAPI 3.x spec into workspace-reviewed tools.",
    category: "Custom",
    scope: "workspace",
    Icon: Server,
    authKind: "bearer",
    transport: "http",
    kind: "openapi-import",
    developer: "Custom",
    website: "OpenAPI spec",
    privacy: "Provider policy",
    terms: "Provider terms",
    usageSuggestion: "Preview generated operations, review read/write classification, then save the connection.",
    tools: [],
    fields: OPENAPI_IMPORT_FIELDS,
  },
  {
    id: "graphql-import",
    name: "GraphQL import",
    description: "Import a GraphQL schema into fixed query and mutation tools.",
    category: "Custom",
    scope: "workspace",
    Icon: Server,
    authKind: "bearer",
    transport: "http",
    kind: "graphql-import",
    developer: "Custom",
    website: "GraphQL endpoint",
    privacy: "Provider policy",
    terms: "Provider terms",
    usageSuggestion: "Preview generated query and mutation tools before enabling the connection.",
    tools: [],
    fields: GRAPHQL_IMPORT_FIELDS,
  },
];

export function catalogIconForSlug(slug: string, authKind: AuthKind): CatalogIcon {
  switch (slug) {
    case "github":
      return SiGithub;
    case "google-workspace":
      return SiGoogle;
    case "google-docs":
      return SiGoogle;
    case "google-sheets":
      return FileSpreadsheet;
    case "google-cloud":
      return SiGooglecloud;
    case "notion":
      return SiNotion;
    case "brevo":
      return SiBrevo;
    case "cloudflare":
      return SiCloudflare;
    case "apollo":
      // No brand mark available for Apollo.io; use a prospecting glyph.
      return Telescope;
    case "agentmail":
      // No brand mark available for AgentMail; use a mail glyph.
      return Mail;
    case "linear":
      return SiLinear;
    case "sentry":
      return SiSentry;
    case "webflow":
      return SiWebflow;
    case "youtube":
      return SiYoutube;
    case "eodhd":
      return ChartLine;
    case "phantombuster":
      return Bot;
    case "fal-ai":
      return ImageIcon;
    case "success-ai":
      return Mail;
    case "canva":
      return SiCanva;
    case "figma":
      return SiFigma;
    case "buffer":
      return SiBuffer;
    case "intercom":
      return SiIntercom;
    case "klaviyo":
      return Mail;
    case "semrush":
      return SiSemrush;
    case "zapier":
      return SiZapier;
    case "elevenlabs":
      return SiElevenlabs;
    case "stripe":
      return SiStripe;
    case "brave":
    case "tavily":
    case "exa":
    case "gemini-search":
    case "firecrawl":
      return Search;
    case "tiktok":
    case "instagram":
      return Video;
    case "slack":
    case "microsoft-teams":
      return MessageSquare;
    case "jira":
    case "trello":
      return Server;
    case "linkedin":
      return Users;
    case "x":
      return SiX;
    case "hubspot":
    case "outlook":
    case "salesforce":
      return BriefcaseBusiness;
    case CUSTOM_MCP_CATALOG_ID:
      return Server;
    case "openapi-import":
    case "graphql-import":
      return Server;
    default:
      return authKind === "bearer" ? KeyRound : Plug;
  }
}

function catalogToolsFromBackendItem(item: WorkspaceIntegrationCatalogItem, localEntry?: CatalogEntry): CatalogEntry["tools"] {
  const tools = Array.isArray(item.tools) ? item.tools : [];
  if (tools.length > 0) {
    return tools.map((tool) => ({
      name: tool.name,
      description: tool.description || "Workspace integration tool.",
      access: tool.access,
      mode: tool.mode,
      source: tool.source,
    }));
  }
  return localEntry?.tools ?? [];
}

function catalogToolManifestFromBackendItem(item: WorkspaceIntegrationCatalogItem, localEntry?: CatalogEntry): CatalogEntry["toolManifest"] {
  const tools = Array.isArray(item.tools) ? item.tools : [];
  if (tools.length > 0) {
    return tools.map((tool) => ({
      name: tool.name,
      description: tool.description || "Workspace integration tool.",
    }));
  }
  return localEntry?.toolManifest;
}

function catalogEntryFromBackendItem(item: WorkspaceIntegrationCatalogItem, localEntry?: CatalogEntry): CatalogEntry {
  return {
    id: item.slug,
    name: item.display_name,
    description: item.description,
    category: item.category,
    scope: localEntry?.scope ?? "workspace",
    connectionSlug: item.connection_slug ?? localEntry?.connectionSlug,
    oauthProviderSlug: localEntry?.oauthProviderSlug,
    googleService: item.google_service ?? localEntry?.googleService,
    Icon: localEntry?.Icon ?? catalogIconForSlug(item.slug, item.auth_kind),
    authKind: item.auth_kind,
    transport: item.transport,
    // A curated remote-MCP entry has a fixed backend-owned endpoint and manifest,
    // so it can be saved from the catalog without probing a user-entered server.
    kind: localEntry?.kind ?? item.slug,
    endpoint: item.remote_url || localEntry?.endpoint,
    tokenType: item.remote_url ? "bearer" : localEntry?.tokenType,
    toolManifest: item.remote_url ? catalogToolManifestFromBackendItem(item, localEntry) : localEntry?.toolManifest,
    developer: item.developer,
    website: item.website,
    privacy: item.privacy,
    terms: item.terms,
    usageSuggestion: localEntry?.usageSuggestion ?? `Use ${item.display_name} through workspace-approved tools.`,
    tools: catalogToolsFromBackendItem(item, localEntry),
    fields: item.remote_url
      ? (item.auth_kind === "bearer" ? CURATED_MCP_BEARER_FIELDS : undefined)
      : localEntry?.fields,
    oauthPermissions: localEntry?.oauthPermissions,
  };
}

function googleWorkspaceAccountLabel(integration: WorkspaceIntegration) {
  return integration.target || integration.display_name || integration.slug;
}

function googleWorkspaceAccountServiceEntryId(serviceEntryId: string, connectionSlug: string) {
  return `${serviceEntryId}--${connectionSlug}`;
}

function googleWorkspaceServiceEntriesForIntegration(entries: Map<string, CatalogEntry>, integration: WorkspaceIntegration) {
  if (integration.kind !== "google_workspace" || integration.slug === GOOGLE_WORKSPACE_CONNECTION_SLUG) {
    return [];
  }
  const accountLabel = googleWorkspaceAccountLabel(integration);
  return [...entries.values()]
    .filter((entry) => (
      entry.googleService
      && catalogEntryConnectionSlug(entry) === GOOGLE_WORKSPACE_CONNECTION_SLUG
      && googleServiceConfiguredForIntegration(entry, integration)
    ))
    .map((entry) => ({
      ...entry,
      id: googleWorkspaceAccountServiceEntryId(entry.id, integration.slug),
      name: `${entry.name} - ${accountLabel}`,
      category: "Added",
      connectionSlug: integration.slug,
      oauthProviderSlug: GOOGLE_WORKSPACE_CONNECTION_SLUG,
    }));
}

export function catalogEntriesFromBackend(
  catalogItems: WorkspaceIntegrationCatalogItem[] | null | undefined,
  integrations: WorkspaceIntegration[],
  localEntries: CatalogEntry[] = CATALOG,
) {
  const localById = new Map(localEntries.map((entry) => [entry.id, entry]));
  const entries = new Map<string, CatalogEntry>();
  if (catalogItems?.length) {
    for (const item of catalogItems) {
      entries.set(item.slug, catalogEntryFromBackendItem(item, localById.get(item.slug)));
    }
    for (const entry of localEntries) {
      if (!entries.has(entry.id)) {
        entries.set(entry.id, entry);
      }
    }
  } else {
    for (const entry of localEntries) {
      entries.set(entry.id, entry);
    }
  }
  for (const integration of integrations) {
    for (const entry of googleWorkspaceServiceEntriesForIntegration(entries, integration)) {
      entries.set(entry.id, entry);
    }
    if (!entries.has(integration.slug)) {
      entries.set(integration.slug, catalogEntryFromIntegration(integration));
    }
  }
  return [...entries.values()];
}

export function catalogEntryConnectionSlug(entry: CatalogEntry | null | undefined) {
  return entry?.connectionSlug ?? entry?.id ?? "";
}

export function googleServiceConfiguredForIntegration(entry: CatalogEntry, integration: WorkspaceIntegration) {
  if (!entry.googleService) return true;
  const status = integration.service_status?.[entry.googleService]?.status;
  if (status) return status !== "off";
  const prefixes: Record<NonNullable<CatalogEntry["googleService"]>, string[]> = {
    gmail: ["gmail_"],
    drive: ["drive_"],
    calendar: ["calendar_"],
    docs: ["docs_"],
    sheets: ["sheets_"],
  };
  const tools = Array.isArray(integration.tools) ? integration.tools : [];
  return tools.some((tool) => prefixes[entry.googleService!].some((prefix) => tool.name.startsWith(prefix)));
}

export function catalogEntryIsImport(entry: CatalogEntry | null | undefined) {
  return entry?.kind === "openapi-import" || entry?.kind === "graphql-import";
}

export function catalogFieldValue(values: Record<string, string>, fieldId: string) {
  return values[fieldId]?.trim() ?? "";
}

export function catalogConfig(entry: CatalogEntry, values: Record<string, string>) {
  const config: Record<string, unknown> = {};
  for (const field of entry.fields ?? []) {
    if (field.secret) continue;
    const value = catalogFieldValue(values, field.id);
    if (value) {
      config[field.configKey ?? field.id] = value;
    }
  }
  return config;
}

export function catalogSecret(entry: CatalogEntry, values: Record<string, string>) {
  const secretField = entry.fields?.find((field) => field.secret);
  return secretField ? catalogFieldValue(values, secretField.id) : "";
}

function slugifyCustomMCP(value: string) {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/g, "");
  return slug || "custom-mcp";
}

export function deriveCustomMCPSlug({
  url,
  serverName,
  existingSlugs,
}: {
  url: string;
  serverName?: string;
  existingSlugs: string[];
}) {
  let hostname = "";
  try {
    hostname = new URL(url).hostname;
  } catch {
    hostname = "";
  }
  const base = slugifyCustomMCP(serverName || hostname || "custom-mcp");
  const used = new Set([...CATALOG.map((entry) => entry.id), ...existingSlugs]);
  if (!used.has(base)) return base;
  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${base}-${index}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

const READ_VERB_SEGMENTS = new Set([
  "get", "list", "search", "find", "read", "fetch", "lookup", "query", "describe",
  "view", "show", "count", "retrieve", "check", "browse", "inspect",
]);
const WRITE_VERB_SEGMENTS = new Set([
  "create", "update", "delete", "remove", "send", "write", "patch", "post", "submit",
  "sync", "move", "duplicate", "insert", "upload", "publish", "archive", "revoke",
  "cancel", "edit", "add", "set", "put", "rename", "merge", "import",
]);

// Real MCP tools are namespaced (notion-search, kv_namespaces_list), so match a
// verb in any name segment. Write verbs win over read verbs.
function customMCPToolIsReadLike(tool: Pick<WorkspaceIntegrationProbeTool, "name" | "description">) {
  const segments = tool.name.toLowerCase().split(/[-_.\s/]+/).filter(Boolean);
  if (segments.some((segment) => WRITE_VERB_SEGMENTS.has(segment))) return false;
  return segments.some((segment) => READ_VERB_SEGMENTS.has(segment));
}

export function customMCPToolsFromProbe(tools: WorkspaceIntegrationProbeTool[]): CatalogEntry["tools"] {
  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description || "Discovered from the remote MCP server.",
    access: customMCPToolIsReadLike(tool) ? "Read" : "Write",
    mode: "Interactive",
  }));
}

export function customMCPInitialPolicy(tools: CatalogEntry["tools"]) {
  const policies: WorkspaceIntegrationPolicy[] = [];
  for (const tool of tools) {
    if (tool.access === "Read" && policies.filter((policy) => policy.policy === "allow").length < MAX_CUSTOM_MCP_ENABLED_TOOLS) {
      policies.push({ tool_name: tool.name, policy: "allow" });
    } else if (tool.access === "Read") {
      policies.push({ tool_name: tool.name, policy: "block" });
    } else {
      policies.push({ tool_name: tool.name, policy: "require_approval" });
    }
  }
  return policyDraftFromPolicies(policies, true);
}

export function customMCPManifestFromProbe(tools: WorkspaceIntegrationProbeTool[]) {
  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description || "Discovered from the remote MCP server.",
    input_schema: tool.inputSchema ?? { type: "object", additionalProperties: true },
    mcp_remote: {
      name: tool.name,
      auth: { type: "bearer", required: false },
    },
  }));
}

export function workspaceIntegrationSnapshotStatus(
  integration: Pick<WorkspaceIntegration, "snapshot">,
  now = new Date(),
): { state: "fresh" | "stale" | "missing"; label: string } {
  const raw = integration.snapshot?.probed_at;
  if (!raw) {
    return { state: "missing", label: "Probe needed" };
  }
  const probedAt = new Date(raw);
  if (Number.isNaN(probedAt.getTime())) {
    return { state: "missing", label: "Probe needed" };
  }
  if (now.getTime() - probedAt.getTime() > CUSTOM_MCP_SNAPSHOT_STALE_MS) {
    return { state: "stale", label: "Refresh recommended" };
  }
  return { state: "fresh", label: "Current" };
}

function catalogEntryFromIntegration(integration: WorkspaceIntegration): CatalogEntry {
  const integrationTools = Array.isArray(integration.tools) ? integration.tools : [];
  const tools = integrationTools.length
    ? integrationTools.map((tool) => ({
      name: tool.name,
      description: tool.description || "Workspace integration tool.",
      access: tool.access,
      mode: tool.mode,
    }))
    : Array.from({ length: integration.tool_count }, (_, index) => ({
      name: `tool_${index + 1}`,
      description: "Tool snapshot is not available in this response.",
      access: "Read" as const,
      mode: "Interactive" as const,
    }));
  return {
    id: integration.slug,
    name: integration.display_name,
    description: integration.target ? `Custom MCP server at ${integration.target}` : "Custom workspace integration.",
    category: "Added",
    scope: "workspace",
    Icon: Server,
    authKind: "bearer",
    transport: integration.transport === "mcp-remote" ? "mcp-remote" : "http",
    kind: integration.kind,
    fields: integration.kind === "custom-mcp" ? CUSTOM_MCP_FIELDS : undefined,
    developer: integration.kind === "custom-mcp" ? "Custom" : integration.display_name,
    website: integration.target || "Workspace integration",
    privacy: "Provider policy",
    terms: "Provider terms",
    usageSuggestion: integration.kind === "custom-mcp"
      ? "Use this custom MCP server through the workspace tools reviewed by an admin."
      : "Use this workspace integration through the enabled tools.",
    tools,
  };
}

export function requiredCatalogFieldsPresent(entry: CatalogEntry | null, values: Record<string, string>) {
  return !(entry?.fields ?? []).some((field) => field.required && !catalogFieldValue(values, field.id));
}

export function googleWorkspaceOAuthPayload(selection: OAuthPermissionSelection) {
  return { permissions: { ...selection } };
}

export type PolicyDraft = {
  allowedTools: string[];
  deniedTools: string[];
  policies: WorkspaceIntegrationPolicy[];
  supportsAsk: boolean;
};

export function normalizeWorkspaceIntegrationPolicyState(value: string | undefined): WorkspaceIntegrationPolicyState {
  if (value === "require_approval" || value === "block") return value;
  return "allow";
}

export function policyDraftFromPolicies(policies: WorkspaceIntegrationPolicy[], supportsAsk: boolean): PolicyDraft {
  const normalized = policies
    .filter((policy) => policy.tool_name.trim())
    .map((policy) => ({
      tool_name: policy.tool_name,
      policy: normalizeWorkspaceIntegrationPolicyState(policy.policy),
    }));
  return {
    allowedTools: normalized.filter((policy) => policy.policy === "allow").map((policy) => policy.tool_name),
    deniedTools: normalized.filter((policy) => policy.policy === "block").map((policy) => policy.tool_name),
    policies: normalized,
    supportsAsk,
  };
}

export function legacyPolicyState(allowed: string[], denied: string[], toolName: string): WorkspaceIntegrationPolicyState {
  if (denied.includes(toolName)) return "block";
  if (allowed.length > 0 && !allowed.includes(toolName)) return "block";
  return "allow";
}

export function policyLabelForState(state: WorkspaceIntegrationPolicyState): "Allowed" | "Ask" | "Blocked" {
  if (state === "require_approval") return "Ask";
  if (state === "block") return "Blocked";
  return "Allowed";
}

// effectivePolicy mirrors the gateway's resolution: deny always wins, and an
// empty allow-list means "allow all"; adding any allow entry switches to
// allow-list mode where unlisted tools are blocked.
export function effectivePolicy(
  allowed: string[],
  denied: string[],
  toolName: string,
  policies?: WorkspaceIntegrationPolicy[],
): "Allowed" | "Ask" | "Blocked" {
  if (policies?.length) {
    const state = policies.find((policy) => policy.tool_name === toolName)?.policy;
    return policyLabelForState(normalizeWorkspaceIntegrationPolicyState(state));
  }
  return policyLabelForState(legacyPolicyState(allowed, denied, toolName));
}
